import React, { useState } from 'react';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';

interface FAQItemProps {
  question: string;
  answer: string;
  isOpen: boolean;
  onClick: () => void;
  delay: string;
  index: number;
}

const FAQItem: React.FC<FAQItemProps> = ({ 
  question, answer, isOpen, onClick, delay, index 
}) => {
  const headingId = `faq-question-${index}`;
  const contentId = `faq-answer-${index}`;

  return (
    <div 
      className="glass-card mb-4 overflow-hidden animate-on-scroll" 
      style={{ transitionDelay: delay }}
    >
      <button
        className="w-full px-6 py-4 text-left flex justify-between items-center"
        onClick={onClick}
        aria-expanded={isOpen}
        aria-controls={contentId}
        id={headingId}
      >
        <h3 className="font-heading text-lg font-semibold">{question}</h3>
        <span className="sr-only">{isOpen ? 'Collapse' : 'Expand'}</span>
        {isOpen ? <ChevronUp size={20} aria-hidden="true" /> : <ChevronDown size={20} aria-hidden="true" />}
      </button>
      
      <div
        id={contentId}
        role="region"
        aria-labelledby={headingId}
        className={`px-6 overflow-hidden transition-all duration-300 ${
          isOpen ? 'max-h-60 pb-6' : 'max-h-0'
        }`}
      >
        <p className="text-text-secondary">{answer}</p>
      </div>
    </div>
  );
};

const FAQ: React.FC = () => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);
  const { t } = useTranslation();
  
  const faqs = [
    {
      question: t('faq.q1.question'),
      answer: t('faq.q1.answer')
    },
    {
      question: t('faq.q2.question'),
      answer: t('faq.q2.answer')
    },
    {
      question: t('faq.q3.question'),
      answer: t('faq.q3.answer')
    },
    {
      question: t('faq.q4.question'),
      answer: t('faq.q4.answer')
    },
    {
      question: t('faq.q5.question'),
      answer: t('faq.q5.answer')
    },
    {
      question: t('faq.q6.question'),
      answer: t('faq.q6.answer')
    },
    {
      question: t('faq.q7.question'),
      answer: t('faq.q7.answer')
    },
    {
      question: t('faq.q8.question'),
      answer: t('faq.q8.answer')
    },
    {
      question: t('faq.q9.question'),
      answer: t('faq.q9.answer')
    },
    {
      question: t('faq.q10.question'),
      answer: t('faq.q10.answer')
    },
    {
      question: t('faq.q11.question'),
      answer: t('faq.q11.answer')
    },
    {
      question: t('faq.q12.question'),
      answer: t('faq.q12.answer')
    }
  ];

  const toggleFAQ = (index: number) => {
    setOpenIndex(openIndex === index ? null : index);
  };

  return (
    <section id="faq" className="section-spacing bg-bg-primary relative" aria-labelledby="faq-title">
      <div className="container-custom max-w-3xl">
        <h2 id="faq-title" className="text-3xl md:text-4xl font-bold mb-16 text-center animate-on-scroll">
          {t('faq.heading')}
        </h2>
        
        <div role="list">
          {faqs.map((faq, index) => (
            <FAQItem
              key={index}
              index={index}
              question={faq.question}
              answer={faq.answer}
              isOpen={openIndex === index}
              onClick={() => toggleFAQ(index)}
              delay={`${0.1 * (index + 1)}s`}
            />
          ))}
        </div>
      </div>
      
      {/* Risk disclosure */}
      <div className="container-custom max-w-3xl mt-16">
        <div className="glass-card p-6 text-center animate-on-scroll" role="alert">
          <h3 className="font-heading font-semibold mb-2">{t('faq.riskDisclosure.heading')}</h3>
          <p className="text-text-secondary text-sm">
            {t('faq.riskDisclosure.text')}
          </p>
        </div>
      </div>
    </section>
  );
};

export default FAQ;